<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';

const { t } = useI18n();
const mensagens = ref([]);
const texto = ref('');
const threadId = ref(null);
const enviando = ref(false);
const integracoesReais = ref(false);
const sessoes = ref([]);
const arquivoInput = ref(null);

const carregarSessoes = async () => {
  try {
    const { data } = await AgenteIaAPI.sessoesDeTeste();
    sessoes.value = data.payload;
  } catch (e) {
    sessoes.value = [];
  }
};

onMounted(carregarSessoes);

// Numa conversa real, handoff_to_human tira o agente da conversa (o status vira
// "aberta") e ele não volta a responder sozinho; o Testar reproduz o silêncio,
// mas precisa dizer o motivo, senão parece que o agente travou.
const textoDaResposta = data => {
  if (data.entregue_a_humano) return t('AGENTE_IA.TESTAR.ENTREGUE_A_HUMANO');
  if (data.suprimida) return t('AGENTE_IA.TESTAR.SUPRIMIDA');
  return data.resposta;
};

const enviar = async ({ arquivo } = {}) => {
  const mensagem = texto.value.trim();
  if (!mensagem && !arquivo) return;
  mensagens.value.push({
    de: 'cliente',
    texto: arquivo ? arquivo.name : mensagem,
  });
  texto.value = '';
  enviando.value = true;
  try {
    const { data } = await AgenteIaAPI.testar({
      mensagem,
      threadId: threadId.value,
      arquivo,
      reais: integracoesReais.value,
    });
    threadId.value = data.thread_id;
    mensagens.value.push({
      de: 'agente',
      texto: textoDaResposta(data),
      ferramentas: data.ferramentas,
      simuladas: data.simuladas,
      fontes: data.fontes,
    });
  } catch (e) {
    mensagens.value.push({
      de: 'erro',
      texto: e?.response?.data?.error || t('AGENTE_IA.TESTAR.ERRO'),
    });
  } finally {
    enviando.value = false;
  }
};

const escolherArquivo = evento => {
  const [arquivo] = evento.target.files || [];
  if (arquivo) enviar({ arquivo });
  evento.target.value = '';
};

const novaConversa = () => {
  threadId.value = null;
  mensagens.value = [];
  carregarSessoes();
};

const continuar = sessao => {
  threadId.value = sessao.thread_id;
  mensagens.value = [];
};
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.TESTAR.TITULO')"
    :description="t('AGENTE_IA.TESTAR.DESCRICAO')"
  >
    <div class="flex flex-col gap-4 lg:flex-row">
      <section
        class="flex flex-col flex-1 gap-3 p-4 rounded-lg border border-n-weak"
      >
        <div class="flex flex-col gap-3 overflow-y-auto min-h-64 max-h-[28rem]">
          <p v-if="!mensagens.length" class="text-sm text-n-slate-11">
            {{ t('AGENTE_IA.TESTAR.VAZIO') }}
          </p>
          <div
            v-for="(mensagem, indice) in mensagens"
            :key="indice"
            class="flex flex-col max-w-[85%] gap-1"
            :class="
              mensagem.de === 'cliente' ? 'self-end items-end' : 'self-start'
            "
          >
            <p
              class="px-3 py-2 text-sm whitespace-pre-wrap rounded-lg"
              :class="{
                'bg-n-brand text-white': mensagem.de === 'cliente',
                'bg-n-alpha-black2 text-n-slate-12': mensagem.de === 'agente',
                'bg-n-ruby-3 text-n-ruby-11': mensagem.de === 'erro',
              }"
            >
              {{ mensagem.texto }}
            </p>
            <p
              v-if="mensagem.ferramentas?.length"
              class="text-xs text-n-slate-11"
            >
              {{
                t('AGENTE_IA.TESTAR.USOU', {
                  ferramentas: mensagem.ferramentas.join(', '),
                })
              }}
            </p>
            <p
              v-if="mensagem.simuladas?.length"
              class="text-xs text-n-amber-11"
            >
              {{
                t('AGENTE_IA.TESTAR.SIMULOU', {
                  ferramentas: mensagem.simuladas.join(', '),
                })
              }}
            </p>
            <p v-if="mensagem.fontes?.length" class="text-xs text-n-slate-11">
              {{
                t('AGENTE_IA.TESTAR.FONTES', {
                  fontes: mensagem.fontes.join(', '),
                })
              }}
            </p>
          </div>
        </div>
        <form class="flex items-center gap-2" @submit.prevent="enviar()">
          <input
            v-model="texto"
            name="mensagem"
            class="flex-1 px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
            :placeholder="t('AGENTE_IA.TESTAR.PLACEHOLDER')"
            :disabled="enviando"
          />
          <input
            ref="arquivoInput"
            type="file"
            class="hidden"
            accept="image/*,audio/*,.pdf,.docx,.txt"
            @change="escolherArquivo"
          />
          <Button
            type="button"
            variant="outline"
            icon="i-lucide-paperclip"
            :disabled="enviando"
            @click="arquivoInput.click()"
          />
          <Button
            type="submit"
            :label="t('AGENTE_IA.TESTAR.ENVIAR')"
            :is-loading="enviando"
          />
        </form>
      </section>

      <aside class="flex flex-col gap-2 lg:w-64">
        <Button
          variant="outline"
          size="sm"
          :label="t('AGENTE_IA.TESTAR.NOVA')"
          @click="novaConversa"
        />
        <label class="flex items-start gap-2 text-xs text-n-slate-11">
          <input v-model="integracoesReais" type="checkbox" class="mt-0.5" />
          <span>{{ t('AGENTE_IA.TESTAR.REAIS') }}</span>
        </label>
        <p class="text-xs text-n-slate-10">
          {{ t('AGENTE_IA.TESTAR.AVISO_RAJADA') }}
        </p>
        <h2 class="mt-2 text-xs font-medium text-n-slate-11">
          {{ t('AGENTE_IA.TESTAR.ANTERIORES') }}
        </h2>
        <button
          v-for="sessao in sessoes"
          :key="sessao.thread_id"
          class="px-2 py-1 text-sm text-left truncate rounded text-n-slate-12 hover:bg-n-alpha-black2"
          :class="{ 'bg-n-alpha-black2': sessao.thread_id === threadId }"
          @click="continuar(sessao)"
        >
          {{ sessao.titulo || sessao.thread_id }}
        </button>
      </aside>
    </div>
  </AgenteIaLayout>
</template>
